import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const DishContext = createContext(undefined);

function emptyCreateDishDraft() {
	return {
		title: "",
		description: "",
		notes: "",
		imageUrl: "",
		tags: [],
	};
}

export const DishProvider = ({ children }) => {
	const [createDishDraft, setCreateDishDraft] = useState(emptyCreateDishDraft);
	const [editDishDraft, setEditDishDraft] = useState({});
	/** Which dish the in-memory edit form was loaded for (only one edit session at a time). */
	const [editDraftDishId, setEditDraftDishId] = useState(null);

	const [dishSearchQuery, setDishSearchQuery] = useState("");
	const [paginatedDishes, setPaginatedDishes] = useState([]);
	const [searchedDishes, setSearchedDishes] = useState([]);
	const [paginationPage, setPaginationPage] = useState(1);
	const [hasMorePaginatedPages, setHasMorePaginatedPages] = useState(true);
	const [hasDishListError, setHasDishListError] = useState(false);
	const [listCacheStale, setListCacheStale] = useState(false);
	/** Title last chosen when opening dish details from the list (or set explicitly), keyed by dishId. */
	const [dishDetailsListPrefetch, setDishDetailsListPrefetch] = useState(null);
	/**
	 * Unified dish details cache last loaded on DishDetails; cleared when leaving details or invalidating list.
	 * Shape: { dishData, recipesData } where dishData is the full dish object and recipesData is the linked-recipes array.
	 */
	const [cachedDishDetails, setCachedDishDetailsState] = useState(null);

	const loadedPaginatedPagesRef = useRef(new Set());

	const updateCreateDishDraft = useCallback((patch) => {
		setCreateDishDraft((prev) => ({ ...prev, ...patch }));
	}, []);

	const resetCreateDishDraft = useCallback(() => {
		setCreateDishDraft(emptyCreateDishDraft());
	}, []);

	const hydrateEditDishDraft = useCallback((dishId, fields) => {
		if (!dishId) {
			return;
		}
		setEditDraftDishId(dishId);
		setEditDishDraft(fields);
	}, []);

	const updateEditDishDraft = useCallback((patch) => {
		setEditDishDraft((prev) => ({ ...prev, ...patch }));
	}, []);

	const resetEditDishDraft = useCallback(() => {
		setEditDishDraft({});
		setEditDraftDishId(null);
	}, []);

	/**
	 * Persist a unified dish-details cache.
	 * @param {{ dishData: object, recipesData: object[] } | null} cachePayload
	 */
	const setCachedDishDetails = useCallback((cachePayload) => {
		setCachedDishDetailsState(cachePayload);
	}, []);

	const clearCachedDishDetails = useCallback(() => {
		setCachedDishDetailsState(null);
	}, []);

	const clearLoadedPaginatedPages = useCallback(() => {
		loadedPaginatedPagesRef.current = new Set();
	}, []);

	const markPaginatedPageLoaded = useCallback((page) => {
		loadedPaginatedPagesRef.current.add(page);
	}, []);

	const isPaginatedPageLoaded = useCallback((page) => {
		return loadedPaginatedPagesRef.current.has(page);
	}, []);

	const invalidateDishListCache = useCallback(() => {
		setListCacheStale(true);
		setPaginatedDishes([]);
		setSearchedDishes([]);
		setDishSearchQuery("");
		setPaginationPage(1);
		setHasMorePaginatedPages(true);
		setHasDishListError(false);
		clearLoadedPaginatedPages();
		clearCachedDishDetails();
	}, [clearLoadedPaginatedPages, clearCachedDishDetails]);

	const markDishListCacheFreshAfterFetch = useCallback(() => {
		setListCacheStale(false);
	}, []);

	useEffect(() => {
		return () => {
			invalidateDishListCache();
		};
	}, [invalidateDishListCache]);

	const value = useMemo(
		() => ({
			createDishDraft,
			updateCreateDishDraft,
			resetCreateDishDraft,
			editDishDraft,
			editDraftDishId,
			hydrateEditDishDraft,
			updateEditDishDraft,
			resetEditDishDraft,
			dishSearchQuery,
			setDishSearchQuery,
			paginatedDishes,
			setPaginatedDishes,
			searchedDishes,
			setSearchedDishes,
			paginationPage,
			setPaginationPage,
			hasMorePaginatedPages,
			setHasMorePaginatedPages,
			hasDishListError,
			setHasDishListError,
			listCacheStale,
			setListCacheStale,
			dishDetailsListPrefetch,
			setDishDetailsListPrefetch,
			cachedDishDetails,
			setCachedDishDetails,
			clearCachedDishDetails,
			invalidateDishListCache,
			markDishListCacheFreshAfterFetch,
			clearLoadedPaginatedPages,
			markPaginatedPageLoaded,
			isPaginatedPageLoaded,
		}),
		[
			createDishDraft,
			updateCreateDishDraft,
			resetCreateDishDraft,
			editDishDraft,
			editDraftDishId,
			hydrateEditDishDraft,
			updateEditDishDraft,
			resetEditDishDraft,
			dishSearchQuery,
			paginatedDishes,
			searchedDishes,
			paginationPage,
			hasMorePaginatedPages,
			hasDishListError,
			listCacheStale,
			dishDetailsListPrefetch,
			setDishDetailsListPrefetch,
			cachedDishDetails,
			setCachedDishDetails,
			clearCachedDishDetails,
			invalidateDishListCache,
			markDishListCacheFreshAfterFetch,
			clearLoadedPaginatedPages,
			markPaginatedPageLoaded,
			isPaginatedPageLoaded,
		]
	);

	return <DishContext.Provider value={value}>{children}</DishContext.Provider>;
};

export const useDish = () => {
	const context = useContext(DishContext);
	if (context === undefined) {
		throw new Error("useDish must be used within a DishProvider");
	}
	return context;
};
